import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { INITIAL_OPACITY, INITIAL_SIZE } from '../../drawing-utility.service';
import { DrawerIconType } from '../../../actions/@base/components/icons/icons.component';

@Component({
  selector: 'txtb-drawer-settings',
  templateUrl: './drawer-settings.component.html',
  styleUrls: ['./drawer-settings.component.scss'],
  standalone: false,
})
export class DrawerSettingsComponent implements OnInit, OnDestroy {
  @Input() activeColor: string | null | undefined;
  @Input() disabledColor: string | null | undefined;
  @Input() isOffline!: boolean;
  @Input() isZoomed = false;
  @Input() color$: Observable<string> = of('#000000');
  @Input() transparency$: Observable<number> = of(0);
  @Input() size$: Observable<number> = of(0);
  @Output() colorChange = new EventEmitter<string>();
  @Output() transparencyChange = new EventEmitter<number>();
  @Output() sizeChange = new EventEmitter<number>();

  readonly iconType: DrawerIconType = 'penSettings';
  tooltip = false;
  colors = [
    { name: 'yellow', value: '#FFFF00' },
    { name: 'orange', value: '#FFBA00' },
    { name: 'red', value: '#FF0000' },
    { name: 'purple', value: '#F500FF' },
    { name: 'blue', value: '#006CFF' },
    { name: 'green', value: '#76FF00' },
    { name: 'black', value: '#000000' },
  ];
  transparency = [
    { iconName: 'opaque', value: INITIAL_OPACITY },
    { iconName: 'semiTransparent', value: 0.6 },
    { iconName: 'transparent', value: 0.2 },
  ];
  thickness = [
    { iconName: 'thinLine', value: INITIAL_SIZE },
    { iconName: 'normalLine', value: 5 },
    { iconName: 'thickLine', value: 10 },
  ];

  subscription = new Subscription();

  thicknessForm = new UntypedFormGroup({
    thickness: new UntypedFormControl(),
  });
  transparencyForm = new UntypedFormGroup({
    transparency: new UntypedFormControl(),
  });

  constructor(private elementRef: ElementRef) {}

  @ViewChild('colorInput') colorInput!: ElementRef<HTMLInputElement>;

  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:mousedown', ['$event'])
  onOutsideClick(event: MouseEvent | TouchEvent): void {
    if (this.tooltip && !this.elementRef.nativeElement.contains(event.target)) {
      this.setTooltip(false);
    }
  }

  ngOnInit(): void {
    this.thicknessForm.setValue({ thickness: INITIAL_SIZE });
    this.transparencyForm.setValue({ transparency: INITIAL_OPACITY });

    this.subscription.add(
      this.thicknessForm.valueChanges.subscribe(({ thickness }) => this.sendSize(thickness))
    );
    this.subscription.add(
      this.transparencyForm.valueChanges.subscribe(({ transparency }) =>
        this.sendTransparency(transparency)
      )
    );
  }

  clickColorInput(): void {
    this.colorInput.nativeElement?.click();
  }

  sendColor(color: string): void {
    this.colorChange.emit(color);
  }

  sendTransparency(transparency: number): void {
    this.transparencyChange.emit(transparency);
  }

  sendSize(thickness: number): void {
    this.sizeChange.emit(thickness);
  }

  emitColorChange(event: Event): void {
    const htmlInput = event.target as HTMLInputElement | undefined;
    this.colorChange.emit(String(htmlInput?.value));
  }

  setTooltip(isOpen: boolean): void {
    this.tooltip = isOpen;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
